import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import AboutZapRF from './dashboard/AboutZapRF';
import PaymentPanel from './dashboard/PaymentPanel';
import LicenseManager from './dashboard/LicenseManager';
import PasswordChange from './dashboard/PasswordChange';
import SupportRequestForm from './dashboard/SupportRequestForm';
import { FaCreditCard, FaCog, FaLock, FaInfoCircle, FaLifeRing } from 'react-icons/fa';
import useCreditMode from '../hooks/useCreditMode';

const Dashboard = () => {
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('about');
  const creditMode = useCreditMode();
  // One label, two products: credits are billing, licenses are entitlements.
  const billingLabel = creditMode ? 'Credits & Billing' : 'Licenses';

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode 
        ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-800' 
        : 'bg-gradient-to-br from-gray-50 via-purple-50 to-gray-100'
    }`}>
      {/* Header Section */}
      <div className="bg-gradient-to-r from-brand-purple to-brand-red">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">
              License Management Dashboard
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Manage your ZapRF licenses, process payments, and monitor your license status all in one place.
            </p>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Tabs Navigation */}
        <div className={`rounded-2xl shadow-xl border mb-8 overflow-hidden transition-all duration-300 ${
          isDarkMode 
            ? 'bg-gray-800/50 backdrop-blur-sm border-gray-700/50' 
            : 'bg-white/70 backdrop-blur-sm border-white/50'
        }`}>
          <div className="flex flex-wrap border-b border-gray-200/20">
            {/* About ZapRF Tab */}
            <button
              onClick={() => setActiveTab('about')}
              className={`flex items-center space-x-2 px-6 py-4 font-medium transition-all duration-200 border-b-2 ${
                activeTab === 'about'
                  ? 'border-brand-purple text-brand-purple bg-brand-purple/5'
                  : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-700/30'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
              }`}
            >
              <FaInfoCircle className="w-4 h-4" />
              <span>About ZapRF</span>
            </button>

            {/* Licenses Tab */}
            <button
              onClick={() => setActiveTab('payment')}
              className={`flex items-center space-x-2 px-6 py-4 font-medium transition-all duration-200 border-b-2 ${
                activeTab === 'payment'
                  ? 'border-brand-red text-brand-red bg-brand-red/5'
                  : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-700/30'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
              }`}
            >
              <FaCreditCard className="w-4 h-4" />
              <span>{billingLabel}</span>
            </button>

            {/* Software Downloads Tab */}
            <button
              onClick={() => setActiveTab('manager')}
              className={`flex items-center space-x-2 px-6 py-4 font-medium transition-all duration-200 border-b-2 ${
                activeTab === 'manager'
                  ? 'border-brand-purple text-brand-purple bg-brand-purple/5'
                  : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-700/30'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
              }`}
            >
              <FaCog className="w-4 h-4" />
              <span>Software Downloads</span>
            </button>

            {/* Security Tab */}
            <button
              onClick={() => setActiveTab('security')}
              className={`flex items-center space-x-2 px-6 py-4 font-medium transition-all duration-200 border-b-2 ${
                activeTab === 'security'
                  ? 'border-blue-500 text-blue-500 bg-blue-500/5'
                  : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-700/30'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
              }`}
            >
              <FaLock className="w-4 h-4" />
              <span>Security</span>
            </button>

            {/* Support Tab */}
            <button
              onClick={() => setActiveTab('support')}
              className={`flex items-center space-x-2 px-6 py-4 font-medium transition-all duration-200 border-b-2 ${
                activeTab === 'support'
                  ? 'border-green-500 text-green-500 bg-green-500/5'
                  : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:bg-gray-700/30'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
              }`}
            >
              <FaLifeRing className="w-4 h-4" />
              <span>Support</span>
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-8">
            {activeTab === 'about' && (
              <div className="animate-fadeIn">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-2 bg-gradient-to-r from-brand-purple to-brand-red rounded-lg">
                    <FaInfoCircle className="w-5 h-5 text-white" />
                  </div>
                  <h2 className={`text-2xl font-semibold ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    About ZapRF
                  </h2>
                </div>
                <AboutZapRF />
              </div>
            )}

            {activeTab === 'payment' && (
              <div className="animate-fadeIn">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-2 bg-gradient-to-r from-brand-red to-brand-red-dark rounded-lg">
                    <FaCreditCard className="w-5 h-5 text-white" />
                  </div>
                  <h2 className={`text-2xl font-semibold ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {billingLabel}
                  </h2>
                </div>
                <PaymentPanel />
              </div>
            )}

            {activeTab === 'manager' && (
              <div className="animate-fadeIn">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-2 bg-gradient-to-r from-brand-purple-dark to-brand-purple rounded-lg">
                    <FaCog className="w-5 h-5 text-white" />
                  </div>
                  <h2 className={`text-2xl font-semibold ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Software Downloads
                  </h2>
                </div>
                <LicenseManager />
              </div>
            )}

            {activeTab === 'security' && (
              <div className="animate-fadeIn">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                    <FaLock className="w-5 h-5 text-white" />
                  </div>
                  <h2 className={`text-2xl font-semibold ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Security Settings
                  </h2>
                </div>
                <PasswordChange />
              </div>
            )}

            {activeTab === 'support' && (
              <div className="animate-fadeIn">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg">
                    <FaLifeRing className="w-5 h-5 text-white" />
                  </div>
                  <h2 className={`text-2xl font-semibold ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Support
                  </h2>
                </div>
                <SupportRequestForm />
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard; 
